const LEVEL_VALUE_PATTERN = /^(L\d+)\s+(.+)$/i;
const LEVEL_COUNTRY_ALIASES: Readonly<Record<string, string>> = {
  AU: 'australia',
  AUSTRALIA: 'australia',
  CR: 'croatia',
  CROATIA: 'croatia',
  CZ: 'czech',
  CZECH: 'czech',
  FR: 'france',
  FRANCE: 'france',
  GE: 'germany',
  GERMANY: 'germany',
  HU: 'hungary',
  HUNGARY: 'hungary',
  ID: 'indonesia',
  INDONESIA: 'indonesia',
  ITALIA: 'italy',
  ITALY: 'italy',
  JP: 'japan',
  JAPAN: 'japan',
  ME: 'mexico',
  MEXICO: 'mexico',
  PH: 'philippines',
  PHILIPPINES: 'philippines',
  RO: 'romania',
  ROMANIA: 'romania',
  ROMÂNIA: 'romania',
  SK: 'slovakia',
  SLOVAKIA: 'slovakia',
  SL: 'slovenia',
  SLOVENIA: 'slovenia',
  VI: 'vietnam',
  VIETNAM: 'vietnam',
};

function normalizeCountry(value: string): string {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toUpperCase();
}

const CANONICAL_COUNTRIES = Array.from(new Set(Object.values(LEVEL_COUNTRY_ALIASES)));

// Country field values may be a code ("VI"), a name ("Vietnam"), or a longer
// name ("Czech Republic"); map all of them to the name used in group labels.
function countryKey(value: string): string | null {
  const normalized = normalizeCountry(value);
  if (!normalized) return null;
  const alias = LEVEL_COUNTRY_ALIASES[normalized];
  if (alias) return alias;
  const lower = normalized.toLowerCase();
  return CANONICAL_COUNTRIES.find((country) => lower.includes(country)) ?? lower;
}

/**
 * Country a Level belongs to: either the short code form ("L1 VI") or a
 * country name anywhere in the label ("L1 Analytic Tool Philippines").
 * Returns null for country-neutral Levels ("L1 LMS", "L2").
 */
function levelCountry(label: string): string | null {
  const match = label.trim().match(LEVEL_VALUE_PATTERN);
  const code = match ? LEVEL_COUNTRY_ALIASES[normalizeCountry(match[2]!)] : undefined;
  if (code) return code;
  const name = normalizeCountry(label).toLowerCase();
  return CANONICAL_COUNTRIES.find((country) => name.includes(country)) ?? null;
}

export interface LevelGroupOption {
  value: string;
  label: string;
}

// ponytail: groups currently expose only names, so match the level and country
// encoded in the configured Level value. Upgrade to explicit group metadata if
// group naming changes or another source of truth becomes available.
export function filterGroupsByLevel<T extends LevelGroupOption>(
  groups: readonly T[],
  selectedLevels: readonly string[],
): T[] {
  const levelConstraints = selectedLevels
    .map((value) => {
      const match = value.trim().match(LEVEL_VALUE_PATTERN);
      if (!match) return null;
      const country = levelCountry(value);
      return country
        ? { levelPrefix: match[1]!.toLowerCase(), country }
        : null;
    })
    .filter((constraint): constraint is { levelPrefix: string; country: string } => Boolean(constraint));
  if (levelConstraints.length === 0) return [...groups];

  return groups.filter((group) => {
    const groupPrefix = group.label.trim().match(/^L\d+\b/i)?.[0].toLowerCase();
    const groupName = normalizeCountry(group.label).toLowerCase();
    return levelConstraints.some((constraint) => (
      constraint.levelPrefix === groupPrefix && groupName.includes(constraint.country)
    ));
  });
}

// ponytail: same name-based matching as Level — a group belongs to a country
// when its label contains that country's name. Upgrade to explicit group
// country metadata if group naming changes.
export function filterGroupsByCountry<T extends LevelGroupOption>(
  groups: readonly T[],
  selectedCountries: readonly string[],
): T[] {
  const countries = selectedCountries
    .map(countryKey)
    .filter((country): country is string => Boolean(country));
  if (countries.length === 0) return [...groups];
  return groups.filter((group) => {
    const groupName = normalizeCountry(group.label).toLowerCase();
    return countries.some((country) => groupName.includes(country));
  });
}

/** Groups must match the selected Country and the selected Level (when set). */
export function filterGroupsByCountryAndLevel<T extends LevelGroupOption>(
  groups: readonly T[],
  selectedCountries: readonly string[],
  selectedLevels: readonly string[],
): T[] {
  return filterGroupsByLevel(filterGroupsByCountry(groups, selectedCountries), selectedLevels);
}

/**
 * Level options ("L3 VI") narrow by the selected Country. Labels that don't
 * follow the "L<n> <country>" pattern are kept so nothing silently disappears.
 */
export function filterLevelsByCountry<T extends LevelGroupOption>(
  levels: readonly T[],
  selectedCountries: readonly string[],
): T[] {
  const countries = selectedCountries
    .map(countryKey)
    .filter((country): country is string => Boolean(country));
  if (countries.length === 0) return [...levels];
  return levels.filter((level) => {
    const country = levelCountry(level.label);
    return !country || countries.includes(country);
  });
}
